import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { ItemFeeSetup, ItemFeeSetupsService } from '@pages/checkout-app/services/item-fee-setups.service';
import { ItemFeeSetupStateService } from '@pages/checkout-app/services/item-fee-setup-state.service';
import { DropdownOption, DropdownSection } from '@storybook/components/dropdown/dropdown.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { ItemFeeRulesComponent } from '../item-fee-rules/item-fee-rules.component';
import { fromUnmatchedFee, toUnmatchedFee } from '../unmatched-fee.util';
import { ItemFeeSetupNameDialogComponent, SetupNameDialogData } from './item-fee-setup-name-dialog.component';

@Component({
  selector: 'item-fee-setup-bar',
  templateUrl: './item-fee-setup-bar.component.html',
  styleUrls: ['./item-fee-setup-bar.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemFeeSetupBarComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() shippingId?: number;

  setups: ItemFeeSetup[] = [];
  setupsUnavailable = false;
  setupsPending = true;
  linkedConfigurationId: number | null = null;
  busy = false;
  private actionError = '';
  private dialogOpen = false;

  setupOptions: DropdownOption[] = [];
  setupSections: DropdownSection[] = [];
  selectedSetupOption: DropdownOption | undefined;
  hasUnsavedSetupChanges = false;

  private readonly destroy$ = new Subject<boolean>();
  private destroyed = false;
  private static readonly SETUPS_UNAVAILABLE =
    'Saved setups could not be loaded. Reload the page before saving this method.';

  constructor(
    private setupsService: ItemFeeSetupsService,
    private toastService: ToastService,
    private setupState: ItemFeeSetupStateService,
    private dialog: MatDialog,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.linkedConfigurationId = this.configurationId;
    this.formGroup
      .get('feePerItem')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh());
    this.setupState.methodSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // The saved method now owns what is on screen, and the listed counts were taken before it did.
      this.linkedConfigurationId = this.configurationId;
      this.loadSetups();
    });
    this.refresh();
    this.loadSetups();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.setupState.reset();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get groupRules(): UntypedFormArray {
    return this.formGroup.get('feePerItem.groupRules') as UntypedFormArray;
  }

  get configurationId(): number | null {
    return this.formGroup.get('feePerItem.configurationId')?.value ?? null;
  }

  get currentSetup(): ItemFeeSetup | undefined {
    return this.setups.find((setup) => setup.id === this.configurationId);
  }

  // The unavailable banner explains why saving is blocked, so it outlives any single action.
  get errorMessage(): string {
    return this.actionError || (this.setupsUnavailable ? ItemFeeSetupBarComponent.SETUPS_UNAVAILABLE : '');
  }

  get rulesInvalid(): boolean {
    return !!this.formGroup.get('feePerItem')?.errors;
  }

  // Counts the OTHER methods, matching the gate on this banner; methodCount includes this one.
  get sharedSetupNotice(): string {
    const others = this.otherMethodCount;
    const scope =
      others === 1
        ? '1 other method. Saving changes applies to both.'
        : `${others} other methods. Saving changes applies to all of them.`;
    return `"${this.currentSetup?.name}" is also used by ${scope} Use the "..." menu to affect only this method.`;
  }

  get otherMethodCount(): number {
    const setup = this.currentSetup;
    if (!setup) {
      return 0;
    }
    return Math.max(0, setup.methodCount - (setup.id === this.linkedConfigurationId ? 1 : 0));
  }

  // The save gate two component levels up reads these synchronously.
  private refresh(): void {
    this.hasUnsavedSetupChanges = this.computeUnsavedSetupChanges();
    this.setupOptions = this.buildSetupOptions();
    this.setupSections = this.buildSetupSections();
    this.selectedSetupOption =
      this.setupOptions.find((option) => option.value === (this.configurationId ?? '')) ??
      this.setupOptions[this.setupOptions.length - 1];
    this.setupState.hasUnsavedSetupChanges = this.hasUnsavedSetupChanges;
    this.setupState.setupsUnavailable = this.setupsUnavailable;
    this.setupState.setupsPending = this.setupsPending;
    this.setupState.setupNames = Object.fromEntries(this.setups.map((setup) => [setup.id, setup.name]));
    this.changeDetector.markForCheck();
  }

  private get hasPrivateConfiguration(): boolean {
    return !this.setupsUnavailable && !!this.configurationId && !this.currentSetup;
  }

  private buildSetupOptions(): DropdownOption[] {
    const saved = this.setups.map((setup) => ({
      label: setup.name,
      helpText:
        setup.id === this.configurationId
          ? `${this.methodsLabel(setup.methodCount)} · Loaded`
          : this.methodsLabel(setup.methodCount),
      value: setup.id as string | number,
    }));

    const unsaved: DropdownOption[] = [];
    if (this.hasPrivateConfiguration) {
      unsaved.push(ItemFeeSetupBarComponent.privateOption(this.configurationId as string | number));
    }
    // Deleting or detaching a setup leaves the rules on the method with no id to point at, so the
    // empty option is the one left selected. "Start from scratch" would misname rules it is keeping.
    const keepsLocalRules = !this.hasPrivateConfiguration && !this.currentSetup && !!this.groupRules?.length;
    unsaved.push(
      keepsLocalRules ? ItemFeeSetupBarComponent.privateOption('') : { label: 'Start from scratch', value: '' },
    );

    return [...saved, ...unsaved];
  }

  private static privateOption(value: string | number): DropdownOption {
    return { label: 'This method only', helpText: 'Rules not shared with other methods', value };
  }

  private buildSetupSections(): DropdownSection[] {
    const sections: DropdownSection[] = [];
    if (this.setups.length) {
      sections.push({ start: 0, end: this.setups.length - 1, name: 'Saved setups' });
    }
    sections.push({ start: this.setups.length, end: this.setupOptions.length - 1, name: 'Not saved' });
    return sections;
  }

  private computeUnsavedSetupChanges(): boolean {
    const setup = this.currentSetup;
    if (!setup) {
      return false;
    }
    return (
      !ItemFeeSetupBarComponent.rulesMatch(setup.rules ?? [], this.groupRules?.value ?? []) ||
      (setup.overlapMode ?? Checkout.RuleOverlapMode.FIRST) !==
        (this.formGroup.get('feePerItem.overlapMode')?.value ?? Checkout.RuleOverlapMode.FIRST) ||
      ItemFeeSetupBarComponent.unmatchedKey(setup.unmatchedFee) !==
        ItemFeeSetupBarComponent.unmatchedKey(toUnmatchedFee(this.formGroup.get('feePerItem.unmatchedFee')?.value))
    );
  }

  private static unmatchedKey(fee: Checkout.UnmatchedItemFee | null | undefined): string {
    return fee ? `${Number(fee.amount) || 0}|${fee.type ?? ''}` : '';
  }

  private static rulesMatch(
    stored: Partial<Checkout.FeePerItemGroupRule>[],
    onScreen: Partial<Checkout.FeePerItemGroupRule>[],
  ): boolean {
    if (stored.length !== onScreen.length) {
      return false;
    }
    return stored.every((rule, index) => this.ruleKey(rule) === this.ruleKey(onScreen[index]));
  }

  private static ruleKey(rule: Partial<Checkout.FeePerItemGroupRule>): string {
    return JSON.stringify([
      rule?.matchBy ?? '',
      rule?.operator ?? '',
      `${rule?.value ?? ''}`.trim(),
      Number(rule?.amount) || 0,
      rule?.type ?? '',
      Number(rule?.step) || 0,
      rule?.stepUnit ?? '',
      Number(rule?.firstNFree) || 0,
      rule?.rounding ?? Checkout.GroupRounding.UP,
    ]);
  }

  async loadSetups(): Promise<void> {
    try {
      const setups = await this.setupsService.list();
      if (this.destroyed) {
        return;
      }
      this.setups = setups;
      this.setupsUnavailable = false;
    } catch {
      if (this.destroyed) {
        return;
      }
      this.setupsUnavailable = true;
    }
    this.setupsPending = false;
    this.refresh();
  }

  async selectSetup(setupId: number | null): Promise<void> {
    // Computed, not read from the cached flag: a stale one loses the edits without a word.
    if (setupId !== this.configurationId && this.computeUnsavedSetupChanges()) {
      const confirmed = await this.confirm({
        title: 'Load another setup?',
        description: `You have unsaved changes to "${this.currentSetup?.name}". Loading another setup replaces them.`,
        confirmText: 'Load setup',
      });
      if (!confirmed) {
        // The picker labelled itself on click; only a new option reference puts it back.
        this.refresh();
        return;
      }
    }
    this.applySetup(setupId);
  }

  applySetup(setupId: number | null): void {
    this.formGroup.get('feePerItem.configurationId')?.setValue(setupId);

    const setup = this.setups.find((candidate) => candidate.id === setupId);
    if (!setup) {
      this.refresh();
      return;
    }

    this.groupRules.clear();
    (setup.rules ?? []).forEach((rule) => this.groupRules.push(ItemFeeRulesComponent.createGroupRuleControl(rule)));
    this.formGroup.get('feePerItem.overlapMode')?.setValue(setup.overlapMode ?? Checkout.RuleOverlapMode.FIRST);
    this.formGroup.get('feePerItem.unmatchedFee')?.setValue(fromUnmatchedFee(setup.unmatchedFee));
    this.formGroup.get('feePerItem').markAsDirty();
    this.refresh();
  }

  async saveChanges(): Promise<void> {
    // Only a named setup can be updated; a private row is not addressable as one.
    if (!this.currentSetup) {
      return;
    }
    await this.run(async () => {
      const updated = await this.setupsService.update(this.configurationId, {
        rules: this.groupRules.value,
        overlapMode: this.formGroup.get('feePerItem.overlapMode')?.value,
        // Explicit null: the fee may have just been switched off.
        unmatchedFee: toUnmatchedFee(this.formGroup.get('feePerItem.unmatchedFee')?.value),
      });
      this.mergeSetup(updated);
      this.announceSaved(updated.name);
    });
  }

  async rename(): Promise<void> {
    if (!this.currentSetup) {
      return;
    }
    const name = await this.askForName({
      title: 'Rename Setup',
      confirmText: 'Save',
      name: this.currentSetup?.name ?? '',
    });
    if (!name) {
      return;
    }
    await this.run(async () => {
      const updated = await this.setupsService.update(this.configurationId, { name });
      this.mergeSetup(updated);
      this.announceSaved(updated.name);
    });
  }

  async duplicate(): Promise<void> {
    const name = await this.askForName({
      title: 'Save As New Setup',
      confirmText: 'Save Setup',
      placeholder: 'e.g. Bags & Luggage, US Domestic',
      help: 'Give this setup a name so you can apply it to other methods later.',
      // Copying a loaded setup starts from its name; a name must still be unique, so it is only a start.
      name: this.currentSetup ? `${this.currentSetup.name} (copy)` : '',
    });
    if (name) {
      await this.saveAsNewSetup(name);
    }
  }

  async saveAsNewSetup(name: string): Promise<void> {
    if (!name?.trim()) {
      this.actionError = 'Give the setup a name first.';
      return;
    }
    await this.run(async () => {
      const created = await this.setupsService.create({
        name: name.trim(),
        rules: this.groupRules.value,
        overlapMode: this.formGroup.get('feePerItem.overlapMode')?.value,
        unmatchedFee: toUnmatchedFee(this.formGroup.get('feePerItem.unmatchedFee')?.value) ?? undefined,
        // Deliberately not linkToShippingId: that repoints the stored method and turns product-group
        // mode on immediately, so live rates would follow the new setup before the method is saved.
      });
      this.setups = [...this.setups, created];
      this.formGroup.get('feePerItem.configurationId')?.setValue(created.id);
      this.announceSaved(created.name);
    });
  }

  async detach(): Promise<void> {
    if (!this.currentSetup) {
      return;
    }
    const confirmed = await this.confirm({
      title: 'Detach and edit locally',
      description:
        `This delivery method keeps its current rules, but stops following "${this.currentSetup?.name}". ` +
        'Changes you make here will no longer affect other methods.',
      confirmText: 'Detach',
    });
    if (!confirmed) {
      return;
    }
    if (!this.shippingId) {
      this.formGroup.get('feePerItem.configurationId')?.setValue(null);
      this.refresh();
      return;
    }
    await this.run(async () => {
      const { configurationId } = await this.setupsService.detach(this.shippingId);
      this.formGroup.get('feePerItem.configurationId')?.setValue(configurationId ?? null);
      await this.loadSetups();
    });
  }

  async remove(): Promise<void> {
    const target = this.currentSetup;
    if (!target) {
      return;
    }
    const deleting = target.id;
    const confirmed = await this.confirm({
      title: `Delete "${target.name}"?`,
      description:
        `This setup is used by ${this.methodsLabel(target.methodCount)}. Each will keep the current rules as a ` +
        'local copy. The setup itself will be removed from the library.',
      confirmText: 'Delete Setup',
      destructive: true,
    });
    if (!confirmed) {
      return;
    }
    await this.run(async () => {
      await this.setupsService.remove(deleting);
      this.setups = this.setups.filter((setup) => setup.id !== deleting);
      if (this.configurationId === deleting) {
        this.formGroup.get('feePerItem.configurationId')?.setValue(null);
      }
    });
  }

  async discard(): Promise<void> {
    const setup = this.currentSetup;
    if (!setup) {
      return;
    }
    const confirmed = await this.confirm({
      title: 'Discard unsaved changes?',
      description: `You have unsaved changes to "${setup.name}". Leave without saving?`,
      confirmText: 'Discard changes',
      cancelText: 'Keep editing',
      destructive: true,
    });
    if (confirmed) {
      this.applySetup(setup.id);
    }
  }

  private methodsLabel(count: number): string {
    return `${count} ${count === 1 ? 'method' : 'methods'}`;
  }

  private askForName(data: SetupNameDialogData): Promise<string | undefined> {
    return this.oneAtATime(() =>
      this.dialog
        .open(ItemFeeSetupNameDialogComponent, {
          backdropClass: 'global_modal_overlay_subdued',
          minWidth: '612px',
          maxWidth: '90vw',
          data,
        })
        .afterClosed()
        .toPromise(),
    );
  }

  // A second click while a dialog is open would open another and save twice.
  private async oneAtATime<T>(open: () => Promise<T>): Promise<T | undefined> {
    if (this.dialogOpen) {
      return undefined;
    }
    this.dialogOpen = true;
    try {
      return await open();
    } finally {
      this.dialogOpen = false;
    }
  }

  private async confirm(data: {
    title: string;
    description: string;
    confirmText: string;
    cancelText?: string;
    destructive?: boolean;
  }): Promise<boolean> {
    const result = await this.oneAtATime(() =>
      this.dialog
        .open(ConfirmationDialogComponent, {
          backdropClass: 'global_modal_overlay_subdued',
          data: { cancelText: 'Cancel', ...data },
        })
        .afterClosed()
        .toPromise(),
    );
    return !!result;
  }

  private announceSaved(name: string): void {
    this.toastService.showToast({ mode: 'success', closeable: false, message: `Setup "${name}" saved` });
  }

  private mergeSetup(updated: ItemFeeSetup): void {
    this.setups = this.setups.map((setup) => (setup.id === updated.id ? updated : setup));
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.busy = true;
    this.actionError = '';
    try {
      await action();
    } catch (error) {
      // The raw HttpErrorResponse message is the URL and status, which means nothing to a merchant.
      this.actionError = error?.error?.message || 'That did not save. Nothing was changed.';
    } finally {
      this.busy = false;
      this.refresh();
    }
  }
}
